import { FC } from 'react'

import { DetailBooleanField, DetailCommentBlock, DetailField, DetailSection } from '../../common'
import { EnergyCertificateDetailsDTO, HeatingSystemItemPayloaDTO } from 'models/purchaseFlow/energyCertificateOfNeedDTO'
import { HeatingFuelType } from 'constants/purchaseFlow'
import { useTranslation } from 'react-i18next'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { GRAY_700 } from 'constants/styling/theme'

const T_PREFIX = 'gallery:assignment_details.energy_certificate'

// Purchase flow translation prefixes for enum values
const PF_PROP = 'purchase_flow:energy_certificate_step.property_information_page'
const PF_LAYOUT = 'purchase_flow:energy_certificate_step.building_layout_page.building_shape'
const PF_CONSTRUCTION = 'purchase_flow:energy_certificate_step.construction_method_page'
const PF_VENTILATION = 'purchase_flow:energy_certificate_step.energy_heating_page.ventilation_system'
const PF_HEATING = 'purchase_flow:energy_certificate_step.energy_heating_page.heating_system'

/** Returns the fuel sub-type translation prefix based on the heating fuel type. */
const getSubTypePath = (heatingType: HeatingFuelType | null | undefined): string | null => {
  switch (heatingType) {
    case 'OIL': return `${PF_HEATING}.oil_fuel_type`
    case 'GAS': return `${PF_HEATING}.gas_fuel_type`
    case 'WOOD_PELLETS_SOLIDS': return `${PF_HEATING}.wood_pelets_fuel_type`
    case 'DISTRICT_HEATING': return `${PF_HEATING}.district_heating_fuel_type`
    case 'HEAT_PUMP': return `${PF_HEATING}.heat_pump_fuel_type`
    case 'ELECTRIC_HEATING': return `${PF_HEATING}.electric_heating_fuel_type`
    case 'INDIVIDUAL_STOVE': return `${PF_HEATING}.individual_stove_fuel_type`
    default: return null
  }
}

interface Props {
  data: EnergyCertificateDetailsDTO
}

export const CertificateOfNeedsSection: FC<Props> = ({ data }) => {
  const { t } = useTranslation(['gallery', 'purchase_flow'])
  const te = (prefix: string, value: string | null | undefined) => value ? t(`${prefix}.${value}`, { defaultValue: value }) : undefined

  return (
    <>
      {/* Property information */}
      <DetailSection title={t(`${T_PREFIX}.property.title`)}>

        <DetailField
          label={t(`${T_PREFIX}.property.building_type`)}
          value={te(`${PF_PROP}.building_type`, data.buildingType)}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.living_units`)}
          value={data.livingUnits}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.building_location`)}
          value={te(`${PF_PROP}.building_location_type`, data.buildingLocation)}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.total_living_area`)}
          value={data.totalLivingArea ? `${data.totalLivingArea} m\u00B2` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.commercial_space`)}
          value={data.commercialSpaceInSquareMeters ? `${data.commercialSpaceInSquareMeters} m\u00B2` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.type_of_use_commercial`)}
          value={data.typeOfUseCommercial}
        />

      </DetailSection>

      {/* Building layout */}
      <DetailSection title={t(`${T_PREFIX}.building_layout.title`)}>

        <DetailField
          label={t(`${T_PREFIX}.building_layout.orientation`)}
          value={te(`${PF_LAYOUT}.orientation_type`, data.orientation)}
        />

        <DetailField
          label={t(`${T_PREFIX}.building_layout.building_year`)}
          value={data.buildingYear}
        />

        <DetailField
          label={t(`${T_PREFIX}.building_layout.building_shape`)}
          value={te(`${PF_LAYOUT}.global_shape_type`, data.buildingShape)}
        />

        {data.wallDimensions?.length > 0 &&
          <DetailField label={t(`${T_PREFIX}.building_layout.wall_dimensions`)}>
            <Stack gap="0.4rem">
              {data.wallDimensions.map((wall, index) => (
                <Stack key={index} direction="row" gap="0.8rem" flexWrap="wrap">

                  <Typography variant="text-sm" color={GRAY_700}>
                    {t(`${T_PREFIX}.building_layout.wall_n`, { n: index + 1 })}:
                  </Typography>

                  <Typography variant="text-sm" fontWeight={600}>
                    {wall.length != null ? `${wall.length} m` : '-'}
                  </Typography>

                  {wall.subsequentlyInsulated &&
                    <Typography variant="text-sm" color={GRAY_700}>
                      ({t(`${T_PREFIX}.building_layout.insulated`)}{wall.wallInsulation != null ? `, ${wall.wallInsulation} cm` : ''})
                    </Typography>
                  }

                  {wall.subsequentlyBuilt &&
                    <Typography variant="text-sm" color={GRAY_700}>
                      ({t(`${T_PREFIX}.building_layout.subsequently_built`)})
                    </Typography>
                  }

                </Stack>
              ))}
            </Stack>
          </DetailField>
        }

      </DetailSection>

      {/* Construction method */}
      <DetailSection title={t(`${T_PREFIX}.construction.title`)}>

        <DetailField
          label={t(`${T_PREFIX}.construction.number_of_floors`)}
          value={data.numberFullFloors}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.floor_height`)}
          value={data.floorHeightMeters != null ? `${data.floorHeightMeters} m` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.roof_condition`)}
          value={te(`${PF_CONSTRUCTION}.roof_condition_type`, data.roofCondition)}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.roof_pitch`)}
          value={te(`${PF_CONSTRUCTION}.roof_pitch_type`, data.roofPitch)}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.subsequent_roof_insulation`)}
          value={data.subsequentRoofInsulation ? `${data.subsequentRoofInsulation} cm` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.basement`)}
          value={te(`${PF_CONSTRUCTION}.basement_type`, data.basement)}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.soil_depth`)}
          value={data.soilDepth != null ? `${data.soilDepth} m` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.heated_basement_area`)}
          value={data.heatedBasementArea != null ? `${data.heatedBasementArea} m\u00B2` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.basement_ceiling_type`)}
          value={te(`${PF_CONSTRUCTION}.basement_ceiling_floor_type`, data.basementCeilingType)}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.subsequent_basement_insulation`)}
          value={data.subsequentBasementInsulation ? `${data.subsequentBasementInsulation} cm` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.window_type`)}
          value={te(`${PF_CONSTRUCTION}.window_type`, data.windowType)}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.window_construction_year`)}
          value={data.windowConstructionYear}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.construction_type`)}
          value={te(`${PF_CONSTRUCTION}.global_shape_type`, data.constructionType)}
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.wall_type`)}
          value={
            data.constructionType === 'MASSIVE_CONSTRUCTION'
              ? te(`${PF_CONSTRUCTION}.solid_construction_type`, data.wallType)
              : te(`${PF_CONSTRUCTION}.lightweight_construction_type`, data.wallType)
          }
        />

        <DetailField
          label={t(`${T_PREFIX}.construction.thermal_bridges`)}
          value={te(`${PF_CONSTRUCTION}.thermal_bridge_type`, data.thermalBridges)}
        />

      </DetailSection>

      {/* Energy & heating */}
      <DetailSection title={t(`${T_PREFIX}.energy.title`)}>

        {!!data.photovoltaics &&
          <>
            <DetailField
              label={t(`${T_PREFIX}.energy.photovoltaic_area`)}
              value={data.photovoltaics.area != null ? `${data.photovoltaics.area} m\u00B2` : undefined}
            />

            <DetailField
              label={t(`${T_PREFIX}.energy.photovoltaic_peak_power`)}
              value={data.photovoltaics.peakPower != null ? `${data.photovoltaics.peakPower} kWp` : undefined}
            />

            <DetailField
              label={t(`${T_PREFIX}.energy.photovoltaic_orientation`)}
              value={te(`${PF_LAYOUT}.orientation_type`, data.photovoltaics.geographicOrientation)}
            />
          </>
        }

        {!data.photovoltaics &&
          <DetailBooleanField
            label={t(`${T_PREFIX}.energy.photovoltaics`)}
            value={false}
          />
        }

        <DetailBooleanField
          label={t(`${T_PREFIX}.energy.window_ventilation`)}
          value={data.hasWindowVentilation}
        />

        <DetailField
          label={t(`${T_PREFIX}.energy.air_distribution`)}
          value={te(`${PF_VENTILATION}.distribution_type`, data.airDistribution)}
        />

        <DetailField
          label={t(`${T_PREFIX}.energy.ventilation_type`)}
          value={te(`${PF_VENTILATION}.ventilation_type`, data.ventilationType)}
        />

        <DetailField
          label={t(`${T_PREFIX}.energy.fan_type`)}
          value={te(`${PF_VENTILATION}.fans_type`, data.fanType)}
        />

        <DetailField
          label={t(`${T_PREFIX}.energy.heat_recovery`)}
          value={te(`${PF_VENTILATION}.heat_recovery_options`, data.heatRecovery)}
        />

      </DetailSection>

      {/* Heating systems */}
      {data.heaters?.map((heater, index) => (
        <HeaterSection key={index} heater={heater} index={index} t={t} te={te} />
      ))}

      {/* Additional information */}
      <DetailSection title={t(`${T_PREFIX}.additional.title`)}>
        <DetailField
          label={t(`${T_PREFIX}.additional.reason`)}
          value={te('purchase_flow:additional_information_page.energy_certificate_reason', data.reasonForEnergyPass)}
        />
      </DetailSection>

      {!!data.messageToExpert &&
        <DetailSection title={t(`${T_PREFIX}.additional.message_to_expert`)}>
          <DetailCommentBlock>{data.messageToExpert}</DetailCommentBlock>
        </DetailSection>
      }
    </>
  )
}

// -- Heater sub-component --

interface HeaterSectionProps {
  heater: HeatingSystemItemPayloaDTO
  index: number
  t: (key: string, options?: Record<string, unknown>) => string
  te: (prefix: string, value: string | null | undefined) => string | undefined
}

const HeaterSection: FC<HeaterSectionProps> = ({ heater, index, t, te }) => {
  const subTypePath = getSubTypePath(heater.heatingType)

  return (
    <DetailSection title={t(`${T_PREFIX}.heating.title_n`, { n: index + 1 })}>

      <DetailField
        label={t(`${T_PREFIX}.heating.fuel_type`)}
        value={te(`${PF_HEATING}.heating_fuel_type`, heater.heatingType)}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.fuel_sub_type`)}
        value={subTypePath ? te(subTypePath, heater.heatingSubType) : heater.heatingSubType?.toString()}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.energy_source`)}
        value={
          heater.heatingType === 'WOOD_PELLETS_SOLIDS'
            ? te(`${PF_HEATING}.wood_pallets_energy_source`, heater.energySource)
            : heater.energySource?.toString()
        }
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.type_designation`)}
        value={heater.typeDesignation?.toString()}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.year_of_construction`)}
        value={heater.yearOfConstruction}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.location`)}
        value={te(`${PF_HEATING}.heater_location_type`, heater.location)}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.night_switch`)}
        value={te(`${PF_HEATING}.night_service_type`, heater.nightSwitch)}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.heat_transfer`)}
        value={te(`${PF_HEATING}.heat_transfer_type`, heater.heatTransfer)}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.heating_pipe`)}
        value={te(`${PF_HEATING}.heat_line_type`, heater.heatingPipe)}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.circulating_pump`)}
        value={te(`${PF_HEATING}.circulating_pump_type`, heater.circulatingPump)}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.power`)}
        value={heater.powerKw ? `${heater.powerKw} kW` : undefined}
      />

      <DetailField
        label={t(`${T_PREFIX}.heating.share_in_total`)}
        value={heater.shareInTotalHeatingPower ? `${heater.shareInTotalHeatingPower}%` : undefined}
      />

      <DetailBooleanField
        label={t(`${T_PREFIX}.heating.hydraulic_balancing`)}
        value={heater.isHydraulicBalancingDone}
      />

      <DetailBooleanField
        label={t(`${T_PREFIX}.heating.solar_thermal_heating`)}
        value={heater.solarThermalHeatingSupport}
      />

      <DetailBooleanField
        label={t(`${T_PREFIX}.heating.solar_thermal_hot_water`)}
        value={heater.solarThermalHotWaterSupport}
      />

      <DetailBooleanField
        label={t(`${T_PREFIX}.heating.central_hot_water`)}
        value={heater.hasCentralHotWaterGeneration}
      />

      {!!heater.centralHotWater &&
        <>
          <DetailField
            label={t(`${T_PREFIX}.heating.hot_water_location`)}
            value={te(`${PF_HEATING}.centralized_water_location_type`, heater.centralHotWater.location)}
          />

          <DetailField
            label={t(`${T_PREFIX}.heating.hot_water_construction_year`)}
            value={heater.centralHotWater.constructionYear}
          />

          <DetailBooleanField
            label={t(`${T_PREFIX}.heating.hot_water_circulation`)}
            value={heater.centralHotWater.hotWaterCirculation}
          />
        </>
      }

      {!!heater.decentralizedHotWater &&
        <>
          <DetailField
            label={t(`${T_PREFIX}.heating.decentralized_energy_source`)}
            value={te(`${PF_HEATING}.generated_by_type`, heater.decentralizedHotWater.energySource)}
          />

          <DetailField
            label={t(`${T_PREFIX}.heating.decentralized_type`)}
            value={te(
              `${PF_HEATING}.decentralized_production_type.decentralized_${heater.decentralizedHotWater.energySource?.toLowerCase()}_heating_type`,
              heater.decentralizedHotWater.type
            )}
          />

          <DetailField
            label={t(`${T_PREFIX}.heating.decentralized_construction_year`)}
            value={heater.decentralizedHotWater.constructionYear}
          />
        </>
      }

    </DetailSection>
  )
}
